import {
  createEntityAdapter,
  EntityState,
  createSelector,
} from "@reduxjs/toolkit";
import { defaultSerializeQueryArgs } from "@reduxjs/toolkit/query/react";
import { LastLocation_Entity } from "../../../entities";
import { customerPortalApi } from "../api/customerPortalApi";
import { SearchMetadata } from "@nerdjs/nerd-core";
import { RootState } from "../types";

const lastLocationsAdapter = createEntityAdapter<LastLocation_Entity>();
const lastLocationsInitialState = lastLocationsAdapter.getInitialState();

export const lastLocationEndpoints = customerPortalApi.injectEndpoints({
  endpoints: (build) => ({
    getLastLocations: build.query<
      EntityState<LastLocation_Entity>,
      Record<string, unknown> | void
    >({
      query: (args) => {
        return {
          url: `lastLocations`,
          params: args,
        };
      },
      serializeQueryArgs: ({ endpointDefinition, endpointName }) =>
        defaultSerializeQueryArgs({
          queryArgs: {},
          endpointDefinition,
          endpointName,
        }),
      transformResponse: (responseData: LastLocation_Entity[]) => {
        return lastLocationsAdapter.setAll(
          lastLocationsInitialState,
          responseData
        );
      },
      providesTags: ["lastLocations"],
    }),
    getLastLocation: build.query<LastLocation_Entity, number>({
      query: (id) => `lastLocations/${id}`,
    }),
    createLastLocation: build.mutation<
      LastLocation_Entity,
      Partial<LastLocation_Entity>
    >({
      query: (body) => ({
        method: "POST",
        body,
        url: `lastLocations`,
      }),
      invalidatesTags: ["lastLocations"],
    }),
    updateLastLocation: build.mutation<
      void,
      { id: number; body: Partial<LastLocation_Entity> }
    >({
      query: (args) => ({
        method: "PUT",
        body: args.body,
        url: `lastLocations/${args.id}`,
      }),
      invalidatesTags: ["lastLocations"],
    }),
    deleteLastLocation: build.mutation<void, number>({
      query: (id) => ({
        method: "DELETE",
        url: `lastLocations/${id}`,
      }),
      invalidatesTags: ["lastLocations"],
    }),
    getLastLocationSearchMetadata: build.query<SearchMetadata, void>({
      query: () => `lastLocations/searchMetadata`,
    }),
  }),
});

export const {
  useGetLastLocationQuery,
  useGetLastLocationsQuery,
  useGetLastLocationSearchMetadataQuery,
  useLazyGetLastLocationsQuery,
  useLazyGetLastLocationQuery,
  useLazyGetLastLocationSearchMetadataQuery,
  useCreateLastLocationMutation,
  useDeleteLastLocationMutation,
  useUpdateLastLocationMutation,
} = lastLocationEndpoints;

export default lastLocationEndpoints;

export const selectLastLocationsResult =
  lastLocationEndpoints.endpoints.getLastLocations.select();

const selectLastLocationsData = createSelector(
  selectLastLocationsResult,
  (lastLocationsResult) => {
    return lastLocationsResult.data;
  }
);

export const {
  selectAll: selectAllLastLocations,
  selectById: selectLastLocationById,
} = lastLocationsAdapter.getSelectors<RootState>((state) => {
  return selectLastLocationsData(state) ?? lastLocationsInitialState;
});
